import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
import { useEffect, useMemo, useState, useReducer } from 'preact/hooks';
import { useDebouncedValue } from '@/hooks';
import { useLocation } from 'preact-iso';
import { routes } from '@/utils/routes';
import { audienceApi } from '@/api/audience';
import { lazy, Suspense } from 'preact/compat';
import { useTheme } from '@/hooks/useTheme';
import { CampaignListContactsSection } from './campaigns/campaignList/CampaignListContactsSection';
import { ContactViewPanel } from './common/ContactViewPanel';
import { usePageHeader } from '@/hooks/usePageHeader';
import { RefreshCw } from 'lucide-preact';
import { useAIChatContextUpdater } from '@/hooks';
import { ChatLocations, ChatEntityTypes, ChatViewModes } from '@/constants/aiChat';
const CampaignListCharts = lazy(() => import('./campaigns/campaignList/CampaignListCharts').then(m => ({ default: m.CampaignListCharts })));
export function AudienceDetail({ clientCompanyId, selectedWorkspaceId, campaignId, fileImportId }) {
    const location = useLocation();
    const { setHeader } = usePageHeader();
    const { resolvedTheme } = useTheme();
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const { updateContext } = useAIChatContextUpdater();
    const [activeTab, setActiveTab] = useState('contacts');
    const [search, setSearch] = useState('');
    const [country, setCountry] = useState('');
    const debouncedSearch = useDebouncedValue(search, 250);
    // Load analytics and contacts
    const loadData = async (isRefresh = false, signal) => {
        // Fallback: if somehow missing, redirect to audience index (could alternatively 404)
        if (!fileImportId) {
            location.route(routes.scoped(clientCompanyId, selectedWorkspaceId).audienceIndex());
            return;
        }
        try {
            if (isRefresh) {
                setRefreshing(true);
            }
            else {
                setInitialLoading(true);
            }
            setError(null);
            const resp = await audienceApi.getAnalytics(clientCompanyId, selectedWorkspaceId, campaignId, fileImportId, signal);
            if (resp.error) {
                setError(resp.error);
                return;
            }
            const data = resp.data;
            setAnalytics(data.Analytics ?? null);
            setContacts(data.FileRows || []);
        }
        catch (e) {
            // Don't show error if request was aborted
            if (e instanceof Error && e.name === 'AbortError')
                return;
            console.error(e);
            setError('Failed to load audience analytics');
        }
        finally {
            if (isRefresh) {
                setRefreshing(false);
            }
            else {
                setInitialLoading(false);
            }
        }
    };
    // Fetch analytics for selected fileImportId (assumed required by route)
    useEffect(() => {
        const abortController = new AbortController();
        loadData(false, abortController.signal);
        return () => abortController.abort();
    }, [fileImportId, campaignId]);
    // Update AI context when analytics data loads
    useEffect(() => {
        if (!analytics)
            return;
        updateContext({
            workspaceId: selectedWorkspaceId,
            campaignId,
            location: ChatLocations.CampaignAudience,
            entityType: ChatEntityTypes.Campaign,
            entityId: campaignId,
            viewMode: ChatViewModes.View,
            availableFields: [
                { name: 'totalContacts', displayName: 'Total Contacts', fieldType: 'number', currentValue: analytics.Total.toString(), isEditable: false, isFocused: false },
                { name: 'contactsLoaded', displayName: 'Contacts Loaded', fieldType: 'number', currentValue: contacts.length.toString(), isEditable: false, isFocused: false },
                { name: 'activeTab', displayName: 'Active Tab', fieldType: 'text', currentValue: activeTab, isEditable: false, isFocused: false },
            ],
            availableActions: [
                'View Contact Details',
                'Filter Contacts',
                'View Analytics',
                'Refresh Data',
            ],
        });
    }, [analytics, contacts.length, activeTab, selectedWorkspaceId, campaignId, updateContext]);
    const countryOptions = useMemo(() => { const set = new Set(); for (const c of contacts) {
        if (c.Country)
            set.add(c.Country);
    } return Array.from(set).sort(); }, [contacts]);
    // City dimension removed (City field dropped)
    const filtered = useMemo(() => {
        return contacts.filter(c => {
            if (debouncedSearch) {
                const q = debouncedSearch.toLowerCase();
                const target = `${c.FirstName || ''} ${c.LastName || ''} ${c.Email || ''} ${c.JobTitle || ''} ${c.CompanyName || ''} ${c.Country || ''}`.toLowerCase();
                if (!target.includes(q))
                    return false;
            }
            if (country && c.Country !== country)
                return false;
            return true;
        });
    }, [contacts, debouncedSearch, country]);
    const palette = ['#2563eb', '#059669', '#f59e0b', '#ef4444', '#7c3aed', '#06b6d4', '#84cc16', '#f97316', '#db2777', '#0ea5e9', '#10b981', '#3b82f6', '#8b5cf6', '#eab308', '#22c55e', '#14b8a6', '#e11d48', '#a855f7', '#f43f5e', '#38bdf8'];
    const nivoTheme = useMemo(() => { const isDark = resolvedTheme === 'dark'; const axisText = isDark ? '#cbd5e1' : '#334155'; const gridLine = isDark ? '#334155' : '#e2e8f0'; const legendText = isDark ? '#e2e8f0' : '#334155'; return { background: 'transparent', text: { fill: axisText }, axis: { domain: { line: { stroke: gridLine } }, ticks: { line: { stroke: gridLine }, text: { fill: axisText } }, legend: { text: { fill: legendText } } }, grid: { line: { stroke: gridLine, strokeDasharray: '4 4' } }, legends: { text: { fill: legendText } }, tooltip: { container: { background: isDark ? '#0f172a' : '#ffffff', color: isDark ? '#e2e8f0' : '#0f172a' } } }; }, [resolvedTheme]);
    // Determine origin from query (?origin=campaign|audience-index)
    const origin = (() => {
        try {
            const url = new URL(location?.url || '/', window.location.origin);
            return url.searchParams.get('origin') || 'audience-index';
        }
        catch {
            return 'audience-index';
        }
    })();
    // Breadcrumbs
    useEffect(() => {
        const r = routes.scoped(clientCompanyId, selectedWorkspaceId);
        const items = origin === 'campaign'
            ? [
                { text: 'Campaigns', href: r.campaignsIndex() },
                { text: 'Campaign', href: r.campaign(campaignId, { tab: 'audience' }) },
                { text: 'Audience' }
            ]
            : [
                { text: 'Audience', href: r.audienceIndex() },
                { text: 'Detail' }
            ];
        setHeader({ items });
    }, [clientCompanyId, selectedWorkspaceId, campaignId, origin]);
    if (initialLoading) {
        return (_jsx("div", { className: "p-6", children: _jsx("div", { className: "flex items-center justify-center py-12", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), _jsx("span", { className: "text-slate-600 dark:text-slate-300", children: "Loading audience analytics..." })] }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "p-6", children: _jsx("div", { className: "text-slate-700 dark:text-slate-200", children: error }) }));
    }
    return (_jsxs("div", { className: "p-6", children: [_jsxs("div", { className: "mb-6", children: [_jsx("h2", { className: "text-2xl font-semibold text-slate-900 dark:text-white", children: "Audience Details" }), _jsx("p", { className: "text-slate-600 dark:text-slate-300 mt-1", children: "Analytics and contacts for the campaign audience." })] }), _jsxs("div", { className: "mb-6 flex items-center gap-2 border-b border-slate-200 dark:border-slate-700", children: [_jsx("button", { type: "button", onClick: () => setActiveTab('contacts'), className: `px-4 py-2 text-sm font-medium -mb-px border-b-2 ${activeTab === 'contacts' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`, children: "Contacts" }), _jsx("button", { type: "button", onClick: () => setActiveTab('analytics'), className: `px-4 py-2 text-sm font-medium -mb-px border-b-2 ${activeTab === 'analytics' ? 'border-primary-600 text-primary-600 dark:text-primary-400' : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'}`, children: "Analytics" })] }), activeTab === 'analytics' && (analytics ? (_jsxs("div", { className: "relative", children: [refreshing && (_jsx("div", { className: "absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg", children: _jsxs("div", { className: "flex items-center gap-3", children: [_jsx("div", { className: "w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" }), _jsx("span", { className: "text-sm text-slate-600 dark:text-slate-300", children: "Refreshing..." })] }) })), _jsx("div", { className: "mb-4 flex justify-end", children: _jsxs("button", { onClick: () => loadData(true), disabled: refreshing, className: "inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-md hover:bg-slate-50 dark:hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium", title: "Refresh from server", children: [_jsx(RefreshCw, { size: 12, className: refreshing ? 'animate-spin' : '' }), "Refresh"] }) }), _jsx(Suspense, { fallback: _jsx("div", { className: "p-4 text-slate-600 dark:text-slate-300", children: "Loading charts\u2026" }), children: _jsx(CampaignListCharts, { analytics: analytics, palette: palette, nivoTheme: nivoTheme }) })] })) : (_jsx("div", { className: "p-6 text-sm text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700", children: "No analytics loaded." }))), activeTab === 'contacts' && (_jsx(CampaignListContactsSection, { filtered: filtered, search: search, setSearch: setSearch, country: country, setCountry: setCountry, countryOptions: countryOptions, clientCompanyId: clientCompanyId, workspaceId: selectedWorkspaceId, campaignId: campaignId, onSelectContact: (c) => setSelectedContact(c), onContactUpdated: (updated) => {
                    setContacts(prev => prev.map(ct => ct.Id === updated.Id ? { ...ct, ...updated } : ct));
                    if (selectedContact?.Id === updated.Id) {
                        setSelectedContact(prev => prev ? { ...prev, ...updated } : null);
                    }
                    // Force re-render after state update
                    queueMicrotask(() => forceUpdate(0));
                }, onRefresh: () => loadData(true), refreshing: refreshing })), selectedContact && (_jsx(ContactViewPanel, { contact: contacts.find(ct => ct.Id === selectedContact.Id) || selectedContact, clientCompanyId: clientCompanyId, workspaceId: selectedWorkspaceId, campaignId: campaignId, onClose: () => setSelectedContact(null), onContactUpdated: (u) => {
                    setContacts(prev => prev.map(c => c.Id === u.Id ? { ...c, ...u } : c));
                    setSelectedContact(sc => sc && sc.Id === u.Id ? { ...sc, ...u } : sc);
                } }))] }));
}
export default AudienceDetail;
